import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Lock, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user } = useAuth();
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [generatingSitemap, setGeneratingSitemap] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Nieuwe wachtwoorden komen niet overeen' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'Wachtwoord moet minimaal 8 karakters lang zijn' });
      return;
    }

    setChangingPassword(true);

    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { withCredentials: true }
      );

      setMessage({ type: 'success', text: 'Wachtwoord succesvol gewijzigd!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage({ 
        type: 'error', 
        text: typeof detail === 'string' ? detail : 'Wachtwoord wijzigen mislukt'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleGenerateSitemap = async () => {
    setMessage({ type: '', text: '' });
    setGeneratingSitemap(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/generate-sitemap`,
        {},
        { withCredentials: true }
      );

      setMessage({ 
        type: 'success', 
        text: `Sitemap gegenereerd! ${response.data.pages_count} pagina&apos;s toegevoegd.` 
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Sitemap genereren mislukt' });
    } finally {
      setGeneratingSitemap(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Terug
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Instellingen</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle>Account Informatie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <p className="text-gray-900">{user?.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Naam</label>
                <p className="text-gray-900">{user?.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Rol</label>
                <p className="text-gray-900 capitalize">{user?.role}</p>
              </div>
            </CardContent>
          </Card>

          {/* Password Change */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Wachtwoord Wijzigen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Huidig Wachtwoord
                  </label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nieuw Wachtwoord
                  </label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimaal 8 karakters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bevestig Nieuw Wachtwoord
                  </label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-blue-700 hover:bg-blue-800"
                >
                  {changingPassword ? 'Wijzigen...' : 'Wachtwoord Wijzigen'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* SEO Tools */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Tools</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Sitemap Genereren</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Genereer een nieuwe sitemap.xml bestand met alle gepubliceerde pagina's.
                  Dit helpt zoekmachines om uw website beter te indexeren.
                </p>
                <Button
                  onClick={handleGenerateSitemap}
                  disabled={generatingSitemap}
                  variant="outline"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generatingSitemap ? 'animate-spin' : ''}`} />
                  {generatingSitemap ? 'Genereren...' : 'Sitemap Genereren'}
                </Button>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-semibold text-gray-900 mb-2">Sitemap Locatie</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Uw sitemap is beschikbaar op:
                </p>
                <code className="block bg-gray-100 p-2 rounded text-sm">
                  {window.location.origin}/sitemap.xml
                </code>
                <p className="text-xs text-gray-500 mt-2">
                  Voeg deze URL toe aan Google Search Console voor betere indexering.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card>
            <CardHeader>
              <CardTitle>Systeem Informatie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">CMS Versie</span>
                <span className="font-semibold">v1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Backend API</span>
                <span className="font-semibold">FastAPI</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Database</span>
                <span className="font-semibold">MongoDB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Frontend</span>
                <span className="font-semibold">React 19</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Settings;
